import { toPersianDigits } from '@saay/formatting';
import { useEffect, useRef, useState } from 'react';

export interface CountdownProps {
  /** Server `expiresAt` (ISO instant). The countdown never extends past it (D-19, FTD §11). */
  readonly expiresAt: string;
  /** Text announced once, through `aria-live`, when the time is up. */
  readonly expiredLabel?: string;
  readonly label: string;
  /** Clock seam for tests; defaults to `Date.now`. */
  readonly now?: () => number;
  readonly onExpire?: () => void;
}

function remainingSeconds(expiresAt: string, now: number): number {
  const expiry = Date.parse(expiresAt);
  return Number.isNaN(expiry) ? 0 : Math.max(0, Math.ceil((expiry - now) / 1000));
}

function mmss(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return toPersianDigits(`${minutes}:${String(seconds % 60).padStart(2, '0')}`);
}

/**
 * A server-driven countdown. The visible timer updates each second but is hidden from screen readers;
 * the live region speaks only once, at expiry. Once expired it stays expired, even if a later
 * `expiresAt` arrives for the same countdown: a new token needs a new countdown (`key`).
 */
export function Countdown({
  expiresAt,
  expiredLabel = 'زمان به پایان رسید.',
  label,
  now = Date.now,
  onExpire,
}: CountdownProps) {
  const initialExpiry = useRef(expiresAt);
  const nowRef = useRef(now);
  nowRef.current = now;
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const firedRef = useRef(false);
  const [seconds, setSeconds] = useState(() => remainingSeconds(initialExpiry.current, now()));

  useEffect(() => {
    function fire(): void {
      if (!firedRef.current) {
        firedRef.current = true;
        onExpireRef.current?.();
      }
    }
    if (remainingSeconds(initialExpiry.current, nowRef.current()) === 0) {
      setSeconds(0);
      fire();
      return undefined;
    }
    const timer = setInterval(() => {
      const left = remainingSeconds(initialExpiry.current, nowRef.current());
      setSeconds(left);
      if (left === 0) {
        clearInterval(timer);
        fire();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const expired = seconds === 0;
  return (
    <span className="saay-countdown" data-expired={expired}>
      <span className="saay-visually-hidden">{label}</span>
      <span aria-hidden="true">{mmss(seconds)}</span>
      <span aria-live="assertive" className="saay-visually-hidden" role="status">
        {expired ? expiredLabel : ''}
      </span>
    </span>
  );
}
